// RecentExportsList — the in-modal "Recent exports" panel shared by the
// contract ExportModal and the Reports tab's ReportExportModal: status pill,
// request time, what was exported, and authenticated download buttons once an
// export is ready. Polls while any export is still pending.
//
// A report export is labelled by the view and range it captured. Rendered
// report PDFs live under a prefix with no expiry, so the retention filter that
// hides aged contract exports never applies to them.
//
// Each host lists only its own kind — the Reports tab's dialog lists report
// exports, the contract export dialog everything else — because report rows
// never expire and would otherwise crowd the other kind out of the five-row
// panel. A visually hidden live region announces an export turning ready,
// since the pill changing colour and text is otherwise silent.

import { useState } from "react";
import { useContractExportList } from "../../../hooks/useContractExports";
import { exportApi } from "../../../services/api";
import type { ContractExport } from "../../../types";

/** Which exports a host lists: `report` for report exports, `contract` for
 *  every other kind (contract exports, issue-register exports and legacy rows
 *  written before kinds existed). */
export type RecentExportsKind = "report" | "contract";

function isKind(exp: ContractExport, kind: RecentExportsKind): boolean {
  return kind === "report" ? exp.kind === "report" : exp.kind !== "report";
}

const READY_ANNOUNCEMENT: Record<RecentExportsKind, string> = {
  report: "Report ready to download",
  contract: "Export ready to download",
};
import type { ReportExportView, ReportRange } from "../../../types/report";

const REPORT_VIEW_LABEL: Record<ReportExportView, string> = {
  summary: "Summary",
  delivery: "Delivery performance",
  kpi: "KPI compliance",
  financial: "Financial performance",
};

const REPORT_RANGE_LABEL: Record<ReportRange, string> = {
  "1m": "Last month",
  "3m": "Last 3 months",
  "6m": "Last 6 months",
  "12m": "Last 12 months",
  all: "Contract to date",
};

const RECORD_TYPE_LABEL: Record<string, string> = {
  milestone: "Milestones only",
  deliverable: "Deliverables only",
};

/** What the export contains, for the history row. */
function describeExport(exp: ContractExport): string {
  if (exp.kind === "report" && exp.report) {
    const { view, range, recordType } = exp.report;
    const parts = ["Report", REPORT_VIEW_LABEL[view]];
    // The summary is a contract-to-date position with no range control.
    if (view !== "summary") parts.push(REPORT_RANGE_LABEL[range]);
    if (view === "delivery" && recordType && RECORD_TYPE_LABEL[recordType]) {
      parts.push(RECORD_TYPE_LABEL[recordType]);
    }
    return parts.join(" · ");
  }
  return exp.formats.join(", ");
}

/** The export named for a download button's accessible name. Report rows
 *  are already described by view and range; contract and issue rows show
 *  their format list on screen, which would read as "Download XLSX, pdf,
 *  xlsx" when spoken, so they are named by kind instead. */
function nameExport(exp: ContractExport): string {
  if (exp.kind === "report") return describeExport(exp);
  return exp.kind === "issue" ? "issue export" : "contract export";
}

// S3 documents bucket has a lifecycle rule that expires the `exports/`
// prefix after this many days. Display the row as "expired" once the
// readyAt timestamp ages past the window — the artefact is gone from S3
// and the download will 404, so flagging it in the UI is more honest
// than showing it as ready.
const EXPORT_RETENTION_DAYS = 30;

type EffectiveExportStatus = ContractExport["status"] | "expired";

function effectiveStatus(exp: ContractExport, now = Date.now()): EffectiveExportStatus {
  if (exp.kind === "report") return exp.status;
  if (exp.status === "ready" && exp.readyAt) {
    const ageMs = now - new Date(exp.readyAt).getTime();
    const ageDays = ageMs / (1000 * 60 * 60 * 24);
    if (ageDays > EXPORT_RETENTION_DAYS) return "expired";
  }
  return exp.status;
}

function statusPillClasses(status: EffectiveExportStatus): string {
  switch (status) {
    case "ready":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300";
    case "failed":
      return "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300";
    case "expired":
      return "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400";
    default:
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function DownloadLink({
  contractId,
  exportId,
  format,
  description,
}: {
  contractId: string;
  exportId: string;
  format: "pdf" | "xlsx";
  description: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setBusy(true);
    setError(null);
    try {
      // Authenticated blob download — S3 host is never exposed. A JSON { url }
      // fallback (large exports) is handled transparently inside downloadExport.
      await exportApi.downloadExport(contractId, exportId, format);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={error ?? undefined}
      aria-label={`Download ${format.toUpperCase()}, ${description}`}
      className="inline-flex min-h-6 items-center rounded px-2 py-1 text-caption text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-50 disabled:cursor-wait"
    >
      {busy ? "…" : format.toUpperCase()}
    </button>
  );
}

export default function RecentExportsList({
  contractId,
  isOpen,
  kind,
}: {
  contractId: string;
  isOpen: boolean;
  kind: RecentExportsKind;
}) {
  const { data, isLoading } = useContractExportList(contractId, isOpen);
  // Drop expired entries entirely — once the artefact is gone from S3
  // (past EXPORT_RETENTION_DAYS) the row has no useful action, so showing
  // it as a tombstone is just noise. The S3 lifecycle rule is the source
  // of truth; we mirror it client-side.
  const items = (data?.items ?? []).filter(
    (exp) => isKind(exp, kind) && effectiveStatus(exp) !== "expired",
  );

  // Announce only a transition observed while open: an export that was
  // already ready when the list loaded has nothing new to say.
  const pendingKey = items
    .filter((exp) => exp.status === "pending")
    .map((exp) => exp.exportId)
    .join("|");
  const [trackedPendingKey, setTrackedPendingKey] = useState(pendingKey);
  const [announcement, setAnnouncement] = useState("");
  if (trackedPendingKey !== pendingKey) {
    const readyIds = new Set(
      items.filter((exp) => exp.status === "ready").map((exp) => exp.exportId),
    );
    const turnedReady = trackedPendingKey
      .split("|")
      .some((id) => id !== "" && readyIds.has(id));
    setTrackedPendingKey(pendingKey);
    if (turnedReady) setAnnouncement(READY_ANNOUNCEMENT[kind]);
  }

  return (
    <section className="border-t border-slate-200 dark:border-slate-700 pt-4">
      <h4 className="text-body-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
        Recent exports
      </h4>
      <p role="status" className="sr-only">
        {announcement}
      </p>
      {isLoading ? (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          Loading…
        </p>
      ) : items.length === 0 ? (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          No exports yet — generate one above.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 5).map((exp) => {
            const status = effectiveStatus(exp);
            const description = describeExport(exp);
            return (
              <li
                key={exp.exportId}
                className="flex items-center justify-between gap-3 rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-caption font-medium ${statusPillClasses(status)}`}
                    >
                      {status}
                    </span>
                    <span
                      title={description}
                      className="truncate text-caption text-slate-500 dark:text-slate-400"
                    >
                      {formatRelative(exp.requestedAt)} · {description}
                    </span>
                  </div>
                  {status === "failed" && exp.failedReason && (
                    <p className="mt-1 truncate text-caption text-rose-600 dark:text-rose-400">
                      {exp.failedReason}
                    </p>
                  )}
                  {status === "expired" && (
                    <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                      Artefacts removed after {EXPORT_RETENTION_DAYS} days — generate a new export to download.
                    </p>
                  )}
                </div>
                {status === "ready" && (
                  <div className="flex shrink-0 gap-1">
                    {exp.formats.map((fmt) => (
                      <DownloadLink
                        key={fmt}
                        contractId={contractId}
                        exportId={exp.exportId}
                        format={fmt}
                        description={nameExport(exp)}
                      />
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
